import { Fragment, ReactNode } from 'react';
import { TooltipContent } from '@design-system';
import { currency } from './formatters';

// Shared "Opening / + accruals / − depletions / Closing" balance tooltip
// used on Pool, Death Benefit, and Surplus cells across strategies.
// Strategies compose one or more BreakdownSection blocks; sections that
// have only an opening/closing with zero deltas can opt to render as a
// compact single-line block.

export interface BreakdownLine {
  label: string;
  value: number;
  // 'add' = green +$; 'subtract' = rose −$; 'neutral' = plain.
  kind?: 'add' | 'subtract' | 'neutral';
}

export interface BreakdownSection {
  // Optional sub-heading (e.g. "Acceleration Benefit"). Omit for a flat
  // single-balance tooltip.
  heading?: string;
  opening: number;
  closing: number;
  // Deltas between open and close — accruals first (positive), then
  // depletions. Lines with value === 0 are filtered out.
  lines?: BreakdownLine[];
  // Optional footer note (e.g. "Not yet engaged", "Floored at residual DB").
  note?: string;
}

interface Props {
  title: string;
  description?: string;
  sections: BreakdownSection[];
  // Optional footer rendered outside the sections, beneath everything.
  footer?: ReactNode;
}

function valueClass(kind: BreakdownLine['kind']): string {
  if (kind === 'add') return 'text-emerald-300';
  if (kind === 'subtract') return 'text-rose-300';
  return 'text-gray-100';
}

function valuePrefix(kind: BreakdownLine['kind']): string {
  if (kind === 'add') return '+';
  if (kind === 'subtract') return '−';
  return '';
}

function renderLine(line: BreakdownLine, key: number | string) {
  return (
    <Fragment key={key}>
      <span className="text-gray-300">
        {valuePrefix(line.kind) && `${valuePrefix(line.kind)} `}
        {line.label}
      </span>
      <span className={`font-semibold tabular-nums ${valueClass(line.kind)}`}>
        {valuePrefix(line.kind)}
        {currency(Math.abs(line.value))}
      </span>
    </Fragment>
  );
}

function Section({ section }: { section: BreakdownSection }) {
  const lines = (section.lines ?? []).filter(l => l.value !== 0);
  return (
    <>
      {section.heading && (
        <div className="col-span-2 mt-1 text-[10px] uppercase tracking-wide text-gray-400">
          {section.heading}
        </div>
      )}
      <span className="text-gray-300">Opening</span>
      <span className="font-semibold tabular-nums">{currency(section.opening)}</span>
      {lines.map((line, i) => renderLine(line, `l-${i}`))}
      <span className="text-gray-300">Closing</span>
      <span className="font-semibold tabular-nums">{currency(section.closing)}</span>
      {section.note && (
        <span className="col-span-2 text-[10px] text-gray-400">{section.note}</span>
      )}
    </>
  );
}

export function BalanceBreakdownTooltip({ title, description, sections, footer }: Props) {
  return (
    <TooltipContent className="max-w-xs text-xs">
      <div className="mb-1 font-semibold">{title}</div>
      {description && (
        <div className="mb-1 text-[10px] text-gray-300">{description}</div>
      )}
      <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
        {sections.map((section, i) => (
          <Section key={i} section={section} />
        ))}
      </div>
      {footer && <div className="mt-1 text-[10px] text-gray-400">{footer}</div>}
    </TooltipContent>
  );
}
